import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from '../shared/core/state';

import * as CourseActions from '../shared/core/state/course/course.actions';
import * as LiveClassActions from '../shared/core/state/live-class/live-class.action';

import { CourseItem } from '../shared/core/state/course/models';
import { StorageService } from '../shared/util/storage.service';
import { CONST } from '../shared/util/const';
import { ActivatedRoute, Router } from '@angular/router';
import {
  LiveClass,
  Meeting,
} from '../shared/core/state/live-class/models/live-class';
import { Resource } from '../shared/core/state/resource/models';
import { MeetingUtility } from '../shared/core/state/live-class/models/live-class.dto';
import { DateTime } from 'luxon';
import { EventInput } from '@fullcalendar/core';

import { createDateWithTimezoneAndTime } from '../shared/util/helper.utils';
import { LiveClassService } from '../shared/core/state/live-class/live-class.service';
import { ToastService } from '@black/ui';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
  selector: 'black-instructor',
  templateUrl: './instructor.component.html',
  styleUrls: ['./instructor.component.css'],
  standalone: false,
  host: {
    ngSkipHydration: 'true',
  },
})
export class InstructorComponent implements OnInit, OnDestroy {
  private store = inject<Store<State>>(Store);
  private storageService = inject(StorageService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private meetingService = inject(LiveClassService);
  private toastService = inject(ToastService);

  courseItems: CourseItem[] = [];
  liveClassItems: LiveClass[] = [];

  selectedLiveClass: LiveClass | null = null;
  currentCalendarEvents: EventInput[] | null = null;

  showInviteModal = false;
  inviteTargetLiveClassId?: string;

  meetingLoading: { [shareLinkId: string]: boolean } = {};
  generatingZoom: { [shareLinkId: string]: boolean } = {};
  generatingRtk: { [shareLinkId: string]: boolean } = {};
  editingDirectLink: { [shareLinkId: string]: boolean } = {};
  directLinkInputs: { [shareLinkId: string]: string } = {};

  openInviteModal(liveClassId?: string): void {
    this.inviteTargetLiveClassId = liveClassId || this.selectedLiveClass?.id;
    this.showInviteModal = true;
  }

  onInviteSuccess(): void {
    this.showInviteModal = false;
  }

  ngOnDestroy(): void {
    this.store.dispatch(CourseActions.resetCourseState());
  }
  ngOnInit(): void {
    this.store.dispatch(CourseActions.fetchAllForInstructor());
    this.store.dispatch(LiveClassActions.fetchAllForInstructor());

    this.store
      .select((state) => state.courses.courseItems) // TODO : fix this : seperate state for instructors is recommended
      .subscribe((items) => {
        this.courseItems = items;
      });

    this.store
      .select((state) => state.liveClass.instructorLiveClasses)
      .subscribe((items) => {
        this.liveClassItems = items;
        const selectedLiveClassId = this.route.snapshot.paramMap.get('id');
        if (selectedLiveClassId)
          this.selectLiveClassWithId(selectedLiveClassId);
      });

    this.route.paramMap.subscribe((params) => {
      const selectedLiveClassId = params.get('id');
      if (selectedLiveClassId) {
        this.selectLiveClassWithId(selectedLiveClassId);
      }
    });
  }

  selectLiveClassWithId(selectedLiveClassId: string) {
    if (selectedLiveClassId) {
      const foundItem = this.liveClassItems.find(
        (liveClass) => liveClass.id === selectedLiveClassId
      );
      if (foundItem) this.selectLiveClass(foundItem);
    }
  }

  editCourse(courseId: string) {
    this.storageService.set(
      CONST.STORAGE_KEYS.CREATE_COURSE_ID,
      courseId
    );
    this.router.navigate(['courses/edit']);
  }

  editLiveClass(id: string | undefined) {
    if (id) this.router.navigate(['live-classes/edit/' + id]);
  }

  navigateToLiveClass(liveClassId?: string, tab?: string) {
    if (liveClassId) {
      this.router.navigate(
        ['dashboard', 'instructor', 'live-class', liveClassId],
        tab ? { fragment: tab } : {}
      );
    }
  }

  selectLiveClass(liveClass: LiveClass) {
    this.selectedLiveClass = liveClass;
    this.currentCalendarEvents = this.getCurrentMeetingEvents();
  }

  deselectLiveClass() {
    this.selectedLiveClass = null;
    this.router.navigate(['dashboard', 'instructor']);
  }

  isMeetingConfigured(meeting: Meeting): boolean {
    if (meeting.directJoinUrl && meeting.directJoinUrl.trim().length > 0) {
      return true;
    }
    if (meeting.provider === 'ZOOM') {
      return !!meeting.zoomMeetingId;
    }
    if (meeting.provider === 'REALTIMEKIT' || meeting.provider === 'DYTE') {
      return !!(meeting.rtkMeetingId || meeting.dyteMeetingId);
    }
    return false;
  }

  joinMeeting(item: Meeting) {
    if (item.directJoinUrl && item.directJoinUrl.trim().length > 0) {
      window.open(item.directJoinUrl.trim(), '_blank', 'noopener,noreferrer');
      return;
    }
    if (!this.isMeetingConfigured(item)) {
      this.toastService.showToast(
        'Meeting is not configured yet. Please generate Zoom or provide a direct join link.',
        'error',
      );
      return;
    }
    if (item.shareLinkId) {
      this.joinMeetingByLinkId(item.shareLinkId, item.provider);
    }
  }

  generateZoomForMeeting(meeting: Meeting) {
    const shareLinkId = meeting.shareLinkId;
    if (!shareLinkId) return;
    this.generatingZoom[shareLinkId] = true;
    this.meetingService.generateZoomMeeting(shareLinkId).subscribe({
      next: () => {
        this.generatingZoom[shareLinkId] = false;
        this.toastService.showToast('Zoom meeting created successfully!', 'success');
        this.store.dispatch(LiveClassActions.fetchAllForInstructor());
      },
      error: (err: HttpErrorResponse) => {
        this.generatingZoom[shareLinkId] = false;
        const msg =
          err?.error?.message ||
          err?.message ||
          'Failed to generate Zoom meeting. Please ensure your Zoom account is linked.';
        this.toastService.showToast(msg, 'error');
      },
    });
  }

  generateRtkForMeeting(meeting: Meeting) {
    const shareLinkId = meeting.shareLinkId;
    if (!shareLinkId) return;
    this.generatingRtk[shareLinkId] = true;
    this.meetingService.generateRealtimeKitMeeting(shareLinkId).subscribe({
      next: () => {
        this.generatingRtk[shareLinkId] = false;
        this.toastService.showToast(
          'RealtimeKit meeting created successfully!',
          'success',
        );
        this.store.dispatch(LiveClassActions.fetchAllForInstructor());
      },
      error: (err: HttpErrorResponse) => {
        this.generatingRtk[shareLinkId] = false;
        const msg =
          err?.error?.message ||
          err?.message ||
          'Failed to generate RealtimeKit meeting.';
        this.toastService.showToast(msg, 'error');
      },
    });
  }

  startEditDirectLink(meeting: Meeting) {
    const shareLinkId = meeting.shareLinkId;
    if (!shareLinkId) return;
    this.directLinkInputs[shareLinkId] = meeting.directJoinUrl || '';
    this.editingDirectLink[shareLinkId] = true;
  }

  cancelEditDirectLink(shareLinkId?: string) {
    if (!shareLinkId) return;
    this.editingDirectLink[shareLinkId] = false;
  }

  saveDirectLink(meeting: Meeting) {
    const shareLinkId = meeting.shareLinkId;
    if (!shareLinkId) return;
    const directUrl = (this.directLinkInputs[shareLinkId] || '').trim();
    this.meetingService
      .updateMeetingDirectLink(shareLinkId, directUrl)
      .subscribe({
        next: () => {
          if (directUrl) {
            meeting.directJoinUrl = directUrl;
            this.toastService.showToast('Direct join link updated!', 'success');
          } else {
            delete meeting.directJoinUrl;
            this.toastService.showToast('Direct join link removed!', 'success');
          }
          this.editingDirectLink[shareLinkId] = false;
        },
        error: (err: HttpErrorResponse) => {
          const msg =
            err?.error?.message || err?.message || 'Failed to update direct link';
          this.toastService.showToast(msg, 'error');
        },
      });
  }

  removeDirectLink(meeting: Meeting) {
    const shareLinkId = meeting.shareLinkId;
    if (!shareLinkId) return;
    this.meetingService.updateMeetingDirectLink(shareLinkId, '').subscribe({
      next: () => {
        delete meeting.directJoinUrl;
        this.editingDirectLink[shareLinkId] = false;
        this.directLinkInputs[shareLinkId] = '';
        this.toastService.showToast('Direct join link removed!', 'success');
      },
      error: (err: HttpErrorResponse) => {
        const msg =
          err?.error?.message || err?.message || 'Failed to remove direct link';
        this.toastService.showToast(msg, 'error');
      },
    });
  }

  isEditingDirectLink(shareLinkId?: string): boolean {
    return !!(shareLinkId && this.editingDirectLink[shareLinkId]);
  }

  isGeneratingZoom(shareLinkId?: string): boolean {
    return !!(shareLinkId && this.generatingZoom[shareLinkId]);
  }

  isGeneratingRtk(shareLinkId?: string): boolean {
    return !!(shareLinkId && this.generatingRtk[shareLinkId]);
  }

  isMeetingLoading(shareLinkId?: string): boolean {
    return !!(shareLinkId && this.meetingLoading[shareLinkId]);
  }

  getDirectLinkInput(shareLinkId?: string): string {
    return shareLinkId ? (this.directLinkInputs[shareLinkId] || '') : '';
  }

  setDirectLinkInput(shareLinkId: string | undefined, value: string): void {
    if (shareLinkId) {
      this.directLinkInputs[shareLinkId] = value;
    }
  }

  joinMeetingByLinkId(shareLinkId: string, meetingProvider = 'DYTE') {
    // Handle meeting join based on provider
    this.meetingLoading[shareLinkId] = true;
    const openJoinLink = () => {
      window.open(
        `./meetings/join/${meetingProvider.toLowerCase()}/` + shareLinkId,
        '_blank',
        'noopener,noreferrer'
      );
      this.meetingLoading[shareLinkId] = false;
    };

    if (meetingProvider === 'ZOOM') {
      this.meetingService.getZoomMeetingInfoFromAPI(shareLinkId).subscribe({
        next: (rawData: { start_url?: string } | null) => {
          const startUrl = rawData?.start_url;
          if (startUrl) {
            window.open(startUrl, '_blank', 'noopener,noreferrer');
          } else {
            openJoinLink();
          }
          this.meetingLoading[shareLinkId] = false;
        },
        error: () => {
          openJoinLink();
          this.meetingLoading[shareLinkId] = false;
        },
      });
      return;
    }

    openJoinLink();
  }

  // helpers

  getResourceImagePath(resource: Resource | string) {
    if (typeof resource !== 'string') {
      return resource.path;
    }
    return '';
  }

  getMeetingDescription(meeting: Meeting, startDate: Date, endDate: Date) {
    const meetingUtility = new MeetingUtility(meeting, startDate, endDate);
    return meetingUtility.toDescription();
  }

  getMeetingInterval(meeting: Meeting, startDate: Date, endDate: Date) {
    const meetingUtility = new MeetingUtility(meeting, startDate, endDate);

    const diffInMinutes = meetingUtility.getSessionInterval();

    if (diffInMinutes < 60) return `${diffInMinutes} Minutes`;

    return `${Math.floor(diffInMinutes / 60)} hour ${
      diffInMinutes % 60
    } Minutes`;
  }

  getStartDate(meeting: Meeting, startDate: Date, endDate: Date) {
    const meetingUtility = new MeetingUtility(meeting, startDate, endDate);

    const startDateTime = meetingUtility.getStartDate();
    const intervalInMinutes = meetingUtility.getSessionInterval();
    const endDateTime = DateTime.fromJSDate(startDateTime)
      .plus({ minutes: intervalInMinutes })
      .toJSDate();

    return [startDateTime, endDateTime];
  }

  getCurrentMeetingEvents(): EventInput[] {
    if (this.selectedLiveClass) {
      return this.selectedLiveClass.meetings.map((meeting) => {
        if (!meeting.rrule && meeting.scheduledDate) {
          const startDate = createDateWithTimezoneAndTime(
            meeting.scheduledDate,
            meeting.timezone,
            meeting.startTime
          );

          return {
            title: `${meeting.name}`,
            color: this.getRandomColorHex(),
            start: startDate,
          };
        }

        return {
          title: `${meeting.name}`,
          rrule: meeting.rrule,
          color: this.getRandomColorHex(),
        };
      });
    }
    return [];
  }

  private getRandomColorHex() {
    return '#' + Math.floor(Math.random() * 16777215).toString(16);
  }
}
